/**
 * Facilities & Maintenance Module — Enterprise Operations Hub.
 *
 * One page, six tabs: Dashboard · Inventory · Assets · Maintenance · Procurement · Suppliers.
 *
 * Mounted at /admin/facilities (super admin + facilities team members).
 * Reuses the unified backend at /api/facilities/*.
 */
import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useOpsRole } from '@/hooks/useOpsRole';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Wrench, Package, Boxes, Truck, ShoppingBag, Building2,
  Plus, Edit3, Trash2, Loader2, AlertTriangle, CheckCircle2,
  ArrowLeft, Activity, Flame, Camera, MapPin, Clock, Users,
} from 'lucide-react';
import { authHeaders } from '@/lib/authToken';
import PrintLabelButton from '@/components/PrintLabelButton';
import { MediaUploadList } from '@/components/MediaUploadList';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}${path}`, { ...opts, headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) } });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || `Request failed (${res.status})`); }
  return res.json();
};

const INV_CATEGORIES = ['it_equipment', 'networking', 'audio', 'lighting', 'instruments', 'office_supplies', 'school_supplies', 'uniforms', 'dining_supplies', 'tools', 'security_equipment', 'medical_supplies', 'consumables', 'furniture', 'cleaning', 'books', 'other'];
const TICKET_PRIORITY = ['low', 'medium', 'high', 'critical'];
const TICKET_SCOPES = ['school', 'church', 'studio', 'housing', 'event', 'campaign', 'transport', 'office', 'other'];
const TICKET_STATUS = ['reported', 'under_review', 'assigned', 'in_progress', 'waiting_parts', 'completed', 'verified', 'closed', 'escalated'];

const PRI_COLOR = {
  low: 'bg-blue-100 text-blue-800', medium: 'bg-amber-100 text-amber-800',
  high: 'bg-rose-100 text-rose-800', critical: 'bg-red-600 text-white animate-pulse',
};

export default function FacilitiesModule() {
  const [sp, setSp] = useSearchParams();
  const tab = sp.get('tab') || 'dashboard';
  const setTab = (v) => setSp({ tab: v });
  // Role-gating — the same permission keys the backend enforces in
  // SYSTEM_CONFIG['facilities']['permissions']. We hide tabs the user
  // can't use so they don't bump into 403s post-click.
  const { can, isMember, loading: roleLoading } = useOpsRole('facilities');
  const visibleTabs = [
    { key: 'dashboard',   label: 'Dashboard',   icon: Activity,    perm: 'fac.view' },
    { key: 'inventory',   label: 'Inventory',   icon: Boxes,       perm: 'fac.inventory' },
    { key: 'assets',      label: 'Assets',      icon: Package,     perm: 'fac.assets' },
    { key: 'maintenance', label: 'Maintenance', icon: Wrench,      perm: 'fac.maintenance' },
    { key: 'procurement', label: 'Procurement', icon: ShoppingBag, perm: 'fac.procurement' },
    { key: 'suppliers',   label: 'Suppliers',   icon: Truck,       perm: 'fac.suppliers' },
  ].filter((t) => can(t.perm) || (t.key === 'dashboard' && isMember) || (t.key === 'maintenance' && can('fac.maintenance.work')));
  // Fallback when permission lookup is still loading: keep all tabs to
  // avoid a layout flash. Server still enforces.
  const tabsToRender = roleLoading ? null : visibleTabs;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="facilities-module">
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-slate-400 hover:text-slate-700"><ArrowLeft className="h-5 w-5" /></Link>
            <div>
              <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2"><Wrench className="h-5 w-5 text-indigo-600" />Facilities &amp; Maintenance</h1>
              <p className="text-xs text-slate-500">Inventory · Assets · Maintenance · Procurement — across the whole ecosystem</p>
            </div>
          </div>
          <Link to="/admin/teams?tab=facilities" className="text-xs font-medium text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50" data-testid="facilities-team-link">
            <Users className="h-3.5 w-3.5" />Manage Team
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className={`grid mb-4 grid-cols-${tabsToRender ? tabsToRender.length : 6}`}>
            {(tabsToRender || [
              { key: 'dashboard',   label: 'Dashboard',   icon: Activity },
              { key: 'inventory',   label: 'Inventory',   icon: Boxes },
              { key: 'assets',      label: 'Assets',      icon: Package },
              { key: 'maintenance', label: 'Maintenance', icon: Wrench },
              { key: 'procurement', label: 'Procurement', icon: ShoppingBag },
              { key: 'suppliers',   label: 'Suppliers',   icon: Truck },
            ]).map((t) => (
              <TabsTrigger key={t.key} value={t.key} data-testid={`fac-tab-${t.key}`}>
                <t.icon className="h-4 w-4 mr-1" />{t.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="dashboard"><DashboardTab /></TabsContent>
          <TabsContent value="inventory"><InventoryTab /></TabsContent>
          <TabsContent value="assets"><AssetsTab /></TabsContent>
          <TabsContent value="maintenance"><MaintenanceTab /></TabsContent>
          <TabsContent value="procurement"><ProcurementTab /></TabsContent>
          <TabsContent value="suppliers"><SuppliersTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

// ==================== DASHBOARD ====================
function DashboardTab() {
  const { data: dash } = useQuery({ queryKey: ['fac-dashboard'], queryFn: () => api('/api/facilities/dashboard') });
  if (!dash) return <div className="p-12 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;
  const c = dash.counts || {};
  const tiles = [
    { label: 'Inventory items', value: c.inventory_items, icon: Boxes, color: 'bg-indigo-100 text-indigo-700' },
    { label: 'Assets', value: c.assets, icon: Package, color: 'bg-purple-100 text-purple-700' },
    { label: 'Open tickets', value: c.open_tickets, icon: Wrench, color: 'bg-amber-100 text-amber-700' },
    { label: 'Critical open', value: c.critical_open, icon: Flame, color: c.critical_open > 0 ? 'bg-red-600 text-white animate-pulse' : 'bg-slate-100 text-slate-500' },
    { label: 'Pending PRs', value: c.pending_prs, icon: ShoppingBag, color: 'bg-emerald-100 text-emerald-700' },
    { label: 'Low stock', value: c.low_stock_items, icon: AlertTriangle, color: c.low_stock_items > 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500' },
  ];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="fac-dashboard-tiles">
        {tiles.map(t => {
          const Icon = t.icon;
          return (
            <Card key={t.label}><CardContent className="p-4 flex items-center gap-3">
              <div className={`p-2 rounded-lg ${t.color}`}><Icon className="h-5 w-5" /></div>
              <div><p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">{t.label}</p><p className="text-2xl font-bold">{t.value ?? 0}</p></div>
            </CardContent></Card>
          );
        })}
      </div>
      {(dash.valuation_by_currency || []).length > 0 && (
        <Card><CardContent className="p-4">
          <p className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-2">Inventory valuation</p>
          <div className="flex flex-wrap gap-3">
            {dash.valuation_by_currency.map(v => (
              <div key={v.currency} className="bg-slate-50 px-4 py-2 rounded-lg">
                <p className="text-[10px] text-slate-500 uppercase">{v.currency}</p>
                <p className="text-xl font-bold text-emerald-700">{v.value.toLocaleString()}</p>
                <p className="text-[10px] text-slate-400">{v.items} item{v.items === 1 ? '' : 's'}</p>
              </div>
            ))}
          </div>
        </CardContent></Card>
      )}
      {(dash.low_stock_preview || []).length > 0 && (
        <Card><CardContent className="p-4">
          <p className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-2 flex items-center gap-1"><AlertTriangle className="h-3 w-3 text-amber-600" />Low stock alert</p>
          <div className="space-y-1">
            {dash.low_stock_preview.map(i => (
              <div key={i.id} className="flex justify-between text-sm border-b last:border-0 py-1">
                <span>{i.name} <span className="text-slate-400 text-xs">({i.sku || '—'})</span></span>
                <span className="text-amber-700 font-semibold">{i.quantity} / reorder {i.reorder_level}</span>
              </div>
            ))}
          </div>
        </CardContent></Card>
      )}
    </div>
  );
}

// ==================== INVENTORY ====================
function InventoryTab() {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [moving, setMoving] = useState(null);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [lowOnly, setLowOnly] = useState(false);
  const url = `/api/facilities/inventory?${catFilter === 'all' ? '' : `category=${catFilter}&`}${search ? `q=${encodeURIComponent(search)}&` : ''}${lowOnly ? 'low_stock=true' : ''}`;
  const { data: items = [], isLoading } = useQuery({ queryKey: ['fac-inventory', catFilter, search, lowOnly], queryFn: () => api(url) });
  const delMut = useMutation({
    mutationFn: (id) => api(`/api/facilities/inventory/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Removed'); qc.invalidateQueries({ queryKey: ['fac-inventory'] }); qc.invalidateQueries({ queryKey: ['fac-dashboard'] }); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-2 flex-1 max-w-xl">
          <Input placeholder="Search name / SKU / barcode…" value={search} onChange={e => setSearch(e.target.value)} className="h-9" data-testid="fac-inv-search" />
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger className="h-9 w-40 text-xs" data-testid="fac-inv-cat"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {INV_CATEGORIES.map(c => <SelectItem key={c} value={c} className="capitalize">{c.replace(/_/g, ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
          <label className="text-xs text-slate-600 flex items-center gap-1.5"><input type="checkbox" checked={lowOnly} onChange={e => setLowOnly(e.target.checked)} className="rounded" data-testid="fac-inv-low" />Low only</label>
        </div>
        <Button onClick={() => setAdding(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="fac-inv-add"><Plus className="h-4 w-4 mr-1" />New item</Button>
      </div>

      {isLoading ? <Card><CardContent className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></CardContent></Card>
       : items.length === 0 ? <Card><CardContent className="p-16 text-center text-slate-400"><Boxes className="h-12 w-12 mx-auto mb-3 opacity-50" /><p className="text-sm">No inventory items</p></CardContent></Card>
       : <Card><CardContent className="p-0"><table className="w-full text-sm">
          <thead><tr className="text-xs uppercase tracking-wide text-slate-500 border-b">
            <th className="text-left py-2 px-3">Name</th><th className="text-left py-2 px-3">SKU</th><th className="text-left py-2 px-3">Category</th><th className="text-left py-2 px-3">Location</th><th className="text-right py-2 px-3">Qty</th><th className="text-right py-2 px-3">Value</th><th className="text-right py-2 px-3"></th>
          </tr></thead>
          <tbody>
            {items.map(i => {
              const low = i.reorder_level > 0 && i.quantity <= i.reorder_level;
              return (
                <tr key={i.id} className={`border-b last:border-0 ${low ? 'bg-amber-50/40' : ''}`} data-testid={`fac-inv-row-${i.id}`}>
                  <td className="py-2 px-3 font-medium">{i.name}</td>
                  <td className="py-2 px-3 text-xs font-mono text-slate-500">{i.sku || '—'}</td>
                  <td className="py-2 px-3 text-xs capitalize">{(i.category || '').replace(/_/g, ' ')}</td>
                  <td className="py-2 px-3 text-xs text-slate-500">{i.location || '—'}</td>
                  <td className={`py-2 px-3 text-right font-semibold ${low ? 'text-amber-700' : ''}`}>{i.quantity} {i.unit_type}{low && <span className="block text-[9px]">LOW</span>}</td>
                  <td className="py-2 px-3 text-right text-xs text-emerald-700">{i.purchase_value > 0 ? `${i.currency} ${(i.purchase_value * i.quantity).toFixed(2)}` : '—'}</td>
                  <td className="py-2 px-3 text-right">
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setMoving(i)} data-testid={`fac-inv-move-${i.id}`}>Move</Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditing(i)} data-testid={`fac-inv-edit-${i.id}`}><Edit3 className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600" onClick={() => { if (window.confirm(`Delete ${i.name}?`)) delMut.mutate(i.id); }} data-testid={`fac-inv-delete-${i.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table></CardContent></Card>}

      {adding && <InventoryDialog onClose={() => setAdding(false)} onSaved={() => { qc.invalidateQueries({ queryKey: ['fac-inventory'] }); qc.invalidateQueries({ queryKey: ['fac-dashboard'] }); setAdding(false); }} />}
      {editing && <InventoryDialog item={editing} onClose={() => setEditing(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ['fac-inventory'] }); setEditing(null); }} />}
      {moving && <StockMoveDialog item={moving} onClose={() => setMoving(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ['fac-inventory'] }); setMoving(null); }} />}
    </div>
  );
}

function InventoryDialog({ item, onClose, onSaved }) {
  const isEdit = !!item;
  const [form, setForm] = useState(item ? {
    name: item.name || '', sku: item.sku || '', barcode: item.barcode || '',
    category: item.category || 'other', quantity: item.quantity || 0, unit_type: item.unit_type || 'pcs',
    location: item.location || '', department: item.department || '',
    purchase_value: item.purchase_value || 0, currency: item.currency || 'USD',
    reorder_level: item.reorder_level || 0, notes: item.notes || '',
  } : { name: '', sku: '', barcode: '', category: 'other', quantity: 0, unit_type: 'pcs', location: '', department: '', purchase_value: 0, currency: 'USD', reorder_level: 0, notes: '' });
  const mut = useMutation({
    mutationFn: () => api(isEdit ? `/api/facilities/inventory/${item.id}` : '/api/facilities/inventory',
      { method: isEdit ? 'PUT' : 'POST', body: JSON.stringify({ ...form, quantity: parseInt(form.quantity, 10) || 0, reorder_level: parseInt(form.reorder_level, 10) || 0, purchase_value: parseFloat(form.purchase_value) || 0 }) }),
    onSuccess: () => { toast.success(isEdit ? 'Saved' : 'Created'); onSaved?.(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="fac-inv-dialog">
        <DialogHeader><DialogTitle>{isEdit ? 'Edit item' : 'New inventory item'}</DialogTitle><DialogDescription>Cross-domain inventory tracked across all sites.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2"><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="fac-inv-name" /></div>
          <div><Label>SKU</Label><Input value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value.toUpperCase() })} /></div>
          <div><Label>Barcode</Label><Input value={form.barcode} onChange={e => setForm({ ...form, barcode: e.target.value })} /></div>
          <div>
            <Label>Category</Label>
            <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="fac-inv-category"><SelectValue /></SelectTrigger>
              <SelectContent>{INV_CATEGORIES.map(c => <SelectItem key={c} value={c} className="capitalize">{c.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Unit type</Label><Input value={form.unit_type} onChange={e => setForm({ ...form, unit_type: e.target.value })} placeholder="pcs / box / kg" /></div>
          <div><Label>Quantity</Label><Input type="number" min={0} value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} /></div>
          <div><Label>Reorder level</Label><Input type="number" min={0} value={form.reorder_level} onChange={e => setForm({ ...form, reorder_level: e.target.value })} /></div>
          <div><Label>Location</Label><Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="Storeroom A" /></div>
          <div><Label>Department</Label><Input value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} /></div>
          <div><Label>Unit value</Label><Input type="number" step="0.01" min={0} value={form.purchase_value} onChange={e => setForm({ ...form, purchase_value: e.target.value })} /></div>
          <div><Label>Currency</Label><Input value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value.toUpperCase() })} /></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { if (!form.name) return toast.error('Name required'); mut.mutate(); }} disabled={mut.isPending} className="bg-indigo-600 hover:bg-indigo-700" data-testid="fac-inv-save">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}{isEdit ? 'Save' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StockMoveDialog({ item, onClose, onSaved }) {
  const [form, setForm] = useState({ kind: 'in', qty: 1, from_location: '', to_location: '', reason: '', reference: '' });
  const mut = useMutation({
    mutationFn: () => api('/api/facilities/stock-movements', { method: 'POST', body: JSON.stringify({ ...form, item_id: item.id, qty: parseInt(form.qty, 10) || 1 }) }),
    onSuccess: (r) => { toast.success(`Moved · stock ${r.before} → ${r.after}`); onSaved?.(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="fac-stock-move-dialog">
        <DialogHeader><DialogTitle>Stock movement — {item.name}</DialogTitle><DialogDescription>Current stock: {item.quantity} {item.unit_type}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Kind</Label>
            <Select value={form.kind} onValueChange={v => setForm({ ...form, kind: v })}>
              <SelectTrigger data-testid="fac-stock-kind"><SelectValue /></SelectTrigger>
              <SelectContent>{['in', 'out', 'transfer', 'adjust', 'damaged', 'lost', 'returned'].map(k => <SelectItem key={k} value={k} className="capitalize">{k}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Quantity *</Label><Input type="number" min={1} value={form.qty} onChange={e => setForm({ ...form, qty: e.target.value })} data-testid="fac-stock-qty" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>From</Label><Input value={form.from_location} onChange={e => setForm({ ...form, from_location: e.target.value })} /></div>
            <div><Label>To</Label><Input value={form.to_location} onChange={e => setForm({ ...form, to_location: e.target.value })} /></div>
          </div>
          <div><Label>Reason / reference</Label><Input value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} placeholder="Shipment received / Issued to dept" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending} data-testid="fac-stock-save">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Record
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== ASSETS ====================
function AssetsTab() {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [servicing, setServicing] = useState(null);
  const [search, setSearch] = useState('');
  const url = `/api/facilities/assets?${search ? `q=${encodeURIComponent(search)}` : ''}`;
  const { data: assets = [], isLoading } = useQuery({ queryKey: ['fac-assets', search], queryFn: () => api(url) });
  const delMut = useMutation({
    mutationFn: (id) => api(`/api/facilities/assets/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Removed'); qc.invalidateQueries({ queryKey: ['fac-assets'] }); qc.invalidateQueries({ queryKey: ['fac-dashboard'] }); },
    onError: (e) => toast.error(e.message),
  });
  const STATUS_COLOR = { in_use: 'bg-emerald-100 text-emerald-800', in_storage: 'bg-slate-100 text-slate-700', under_repair: 'bg-amber-100 text-amber-800', retired: 'bg-rose-100 text-rose-800', lost: 'bg-red-100 text-red-800' };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Input placeholder="Search name / tag / serial…" value={search} onChange={e => setSearch(e.target.value)} className="h-9 max-w-md" data-testid="fac-asset-search" />
        <Button onClick={() => setAdding(true)} className="bg-purple-600 hover:bg-purple-700" data-testid="fac-asset-add"><Plus className="h-4 w-4 mr-1" />New asset</Button>
      </div>
      {isLoading ? <Card><CardContent className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></CardContent></Card>
       : assets.length === 0 ? <Card><CardContent className="p-16 text-center text-slate-400"><Package className="h-12 w-12 mx-auto mb-3 opacity-50" /><p className="text-sm">No assets yet</p></CardContent></Card>
       : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {assets.map(a => (
            <Card key={a.id} className="hover:shadow-md transition" data-testid={`fac-asset-${a.id}`}>
              <CardContent className="p-3">
                <div className="flex items-start gap-3">
                  {a.image_url ? <img src={a.image_url} alt="" className="h-14 w-14 object-cover rounded-lg shrink-0" /> : <div className="h-14 w-14 rounded-lg bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center shrink-0"><Package className="h-6 w-6 text-purple-700" /></div>}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{a.name}</p>
                    {a.asset_tag && <p className="text-[10px] font-mono text-purple-700">{a.asset_tag}</p>}
                    <p className="text-[11px] text-slate-500">{a.brand} {a.model}</p>
                    <div className="flex gap-1 mt-1 flex-wrap">
                      <Badge className={`text-[9px] capitalize ${STATUS_COLOR[a.status] || ''}`}>{(a.status || '').replace('_', ' ')}</Badge>
                      <Badge variant="outline" className="text-[9px] capitalize">{(a.health || '').replace('_', ' ')}</Badge>
                    </div>
                  </div>
                </div>
                {a.assigned_user_name && <p className="text-[11px] text-slate-500 mt-2"><Users className="h-3 w-3 inline mr-1" />{a.assigned_user_name}</p>}
                {a.location && <p className="text-[11px] text-slate-500"><MapPin className="h-3 w-3 inline mr-1" />{a.location}</p>}
                <div className="flex justify-end gap-1 mt-2 pt-2 border-t">
                  <PrintLabelButton entity="asset" id={a.id} name={a.name} />
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setServicing(a)} data-testid={`fac-asset-service-${a.id}`}>Service</Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditing(a)} data-testid={`fac-asset-edit-${a.id}`}><Edit3 className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600" onClick={() => { if (window.confirm(`Delete ${a.name}?`)) delMut.mutate(a.id); }} data-testid={`fac-asset-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>}
      {adding && <AssetDialog onClose={() => setAdding(false)} onSaved={() => { qc.invalidateQueries({ queryKey: ['fac-assets'] }); qc.invalidateQueries({ queryKey: ['fac-dashboard'] }); setAdding(false); }} />}
      {editing && <AssetDialog asset={editing} onClose={() => setEditing(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ['fac-assets'] }); setEditing(null); }} />}
      {servicing && <AssetServiceDialog asset={servicing} onClose={() => setServicing(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ['fac-assets'] }); setServicing(null); }} />}
    </div>
  );
}

function AssetDialog({ asset, onClose, onSaved }) {
  const isEdit = !!asset;
  const [form, setForm] = useState(asset ? {
    name: asset.name || '', asset_tag: asset.asset_tag || '', serial_number: asset.serial_number || '',
    category: asset.category || 'other', brand: asset.brand || '', model: asset.model || '',
    location: asset.location || '', assigned_user_name: asset.assigned_user_name || '',
    status: asset.status || 'in_use', health: asset.health || 'healthy',
    purchase_value: asset.purchase_value || 0, currency: asset.currency || 'USD',
    warranty_expiry: asset.warranty_expiry || '',
  } : { name: '', asset_tag: '', serial_number: '', category: 'other', brand: '', model: '', location: '', assigned_user_name: '', status: 'in_use', health: 'healthy', purchase_value: 0, currency: 'USD', warranty_expiry: '' });
  const mut = useMutation({
    mutationFn: () => api(isEdit ? `/api/facilities/assets/${asset.id}` : '/api/facilities/assets',
      { method: isEdit ? 'PUT' : 'POST', body: JSON.stringify({ ...form, purchase_value: parseFloat(form.purchase_value) || 0 }) }),
    onSuccess: () => { toast.success(isEdit ? 'Saved' : 'Created'); onSaved?.(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="fac-asset-dialog">
        <DialogHeader><DialogTitle>{isEdit ? 'Edit asset' : 'New asset'}</DialogTitle><DialogDescription>Fixed-asset registry with lifecycle tracking.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2"><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="fac-asset-name" /></div>
          <div><Label>Asset tag</Label><Input value={form.asset_tag} onChange={e => setForm({ ...form, asset_tag: e.target.value.toUpperCase() })} placeholder="CAM-001" /></div>
          <div><Label>Serial</Label><Input value={form.serial_number} onChange={e => setForm({ ...form, serial_number: e.target.value })} /></div>
          <div>
            <Label>Category</Label>
            <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{INV_CATEGORIES.map(c => <SelectItem key={c} value={c} className="capitalize">{c.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Brand</Label><Input value={form.brand} onChange={e => setForm({ ...form, brand: e.target.value })} /></div>
          <div><Label>Model</Label><Input value={form.model} onChange={e => setForm({ ...form, model: e.target.value })} /></div>
          <div><Label>Location</Label><Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} /></div>
          <div><Label>Assigned to</Label><Input value={form.assigned_user_name} onChange={e => setForm({ ...form, assigned_user_name: e.target.value })} /></div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{['in_use', 'in_storage', 'under_repair', 'lost', 'retired'].map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Health</Label>
            <Select value={form.health} onValueChange={v => setForm({ ...form, health: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{['healthy', 'needs_service', 'under_repair', 'critical', 'retired'].map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Purchase value</Label><Input type="number" step="0.01" min={0} value={form.purchase_value} onChange={e => setForm({ ...form, purchase_value: e.target.value })} /></div>
          <div><Label>Warranty expiry</Label><Input type="date" value={form.warranty_expiry} onChange={e => setForm({ ...form, warranty_expiry: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { if (!form.name) return toast.error('Name required'); mut.mutate(); }} disabled={mut.isPending} className="bg-purple-600 hover:bg-purple-700" data-testid="fac-asset-save">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}{isEdit ? 'Save' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssetServiceDialog({ asset, onClose, onSaved }) {
  const [form, setForm] = useState({ summary: '', cost: 0, currency: 'USD', technician_name: '' });
  const mut = useMutation({
    mutationFn: () => api(`/api/facilities/assets/${asset.id}/service`, { method: 'POST', body: JSON.stringify({ ...form, cost: parseFloat(form.cost) || 0 }) }),
    onSuccess: () => { toast.success('Service logged · health reset to healthy'); onSaved?.(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="fac-asset-service-dialog">
        <DialogHeader><DialogTitle>Log service — {asset.name}</DialogTitle><DialogDescription>Record what was done. Last service: {asset.last_serviced_at?.slice(0, 10) || 'never'}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Summary *</Label><Textarea rows={3} value={form.summary} onChange={e => setForm({ ...form, summary: e.target.value })} placeholder="Replaced battery, cleaned sensor…" /></div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <div><Label>Cost</Label><Input type="number" step="0.01" min={0} value={form.cost} onChange={e => setForm({ ...form, cost: e.target.value })} /></div>
            <div><Label>Currency</Label><Input value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value.toUpperCase() })} /></div>
            <div><Label>Technician</Label><Input value={form.technician_name} onChange={e => setForm({ ...form, technician_name: e.target.value })} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { if (!form.summary) return toast.error('Summary required'); mut.mutate(); }} disabled={mut.isPending}>
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Log service
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== MAINTENANCE ====================
function MaintenanceTab() {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priFilter, setPriFilter] = useState('all');
  const url = `/api/facilities/maintenance?${statusFilter === 'all' ? '' : `status=${statusFilter}&`}${priFilter === 'all' ? '' : `priority=${priFilter}`}`;
  const { data: tickets = [], isLoading } = useQuery({ queryKey: ['fac-tickets', statusFilter, priFilter], queryFn: () => api(url) });
  const updMut = useMutation({
    mutationFn: ({ id, payload }) => api(`/api/facilities/maintenance/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
    onSuccess: () => { toast.success('Updated'); qc.invalidateQueries({ queryKey: ['fac-tickets'] }); qc.invalidateQueries({ queryKey: ['fac-dashboard'] }); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-40 text-xs" data-testid="fac-tk-status"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem>{TICKET_STATUS.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={priFilter} onValueChange={setPriFilter}>
            <SelectTrigger className="h-9 w-32 text-xs" data-testid="fac-tk-priority"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All priorities</SelectItem>{TICKET_PRIORITY.map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={() => setAdding(true)} className="bg-amber-600 hover:bg-amber-700" data-testid="fac-tk-add"><Plus className="h-4 w-4 mr-1" />New ticket</Button>
      </div>
      {isLoading ? <Card><CardContent className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></CardContent></Card>
       : tickets.length === 0 ? <Card><CardContent className="p-16 text-center text-slate-400"><Wrench className="h-12 w-12 mx-auto mb-3 opacity-50" /><p className="text-sm">No tickets</p></CardContent></Card>
       : <div className="space-y-2">
          {tickets.map(t => (
            <Card key={t.id} className={`border-l-4 ${t.priority === 'critical' ? 'border-l-red-700' : t.priority === 'high' ? 'border-l-rose-500' : t.priority === 'medium' ? 'border-l-amber-500' : 'border-l-blue-500'}`} data-testid={`fac-tk-${t.id}`}>
              <CardContent className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-sm">{t.title}</p>
                      <Badge className={`text-[10px] ${PRI_COLOR[t.priority]} capitalize`}>{t.priority}</Badge>
                      <Badge variant="outline" className="text-[10px] capitalize">{(t.status || '').replace('_', ' ')}</Badge>
                      <Badge variant="outline" className="text-[10px] capitalize bg-slate-50">{t.scope_type}{t.scope_label ? ` · ${t.scope_label}` : ''}</Badge>
                    </div>
                    {t.description && <p className="text-xs text-slate-600 mt-1">{t.description}</p>}
                    <div className="flex flex-wrap items-center gap-3 mt-1 text-[11px] text-slate-500">
                      {t.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{t.location}</span>}
                      {t.reporter_name && <span className="flex items-center gap-1"><Users className="h-3 w-3" />{t.reporter_name}</span>}
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(t.created_at).toLocaleString()}</span>
                      {(t.photo_urls || []).length > 0 && <span className="flex items-center gap-1 text-indigo-600"><Camera className="h-3 w-3" />{t.photo_urls.length}</span>}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {t.status !== 'closed' && t.status !== 'completed' && (
                      <Select value="" onValueChange={(v) => updMut.mutate({ id: t.id, payload: { status: v } })}>
                        <SelectTrigger className="h-7 w-32 text-xs" data-testid={`fac-tk-status-${t.id}`}><SelectValue placeholder="Advance →" /></SelectTrigger>
                        <SelectContent>{TICKET_STATUS.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>}
      {adding && <TicketDialog onClose={() => setAdding(false)} onSaved={() => { qc.invalidateQueries({ queryKey: ['fac-tickets'] }); qc.invalidateQueries({ queryKey: ['fac-dashboard'] }); setAdding(false); }} />}
    </div>
  );
}

export function TicketDialog({ onClose, onSaved, defaultScope }) {
  const [form, setForm] = useState({
    title: '', description: '', priority: 'medium',
    scope_type: defaultScope?.type || 'other', scope_id: defaultScope?.id || '',
    scope_label: defaultScope?.label || '', location: '', photo_urls: '',
  });
  const mut = useMutation({
    mutationFn: () => api('/api/facilities/maintenance', { method: 'POST', body: JSON.stringify({
      ...form, photo_urls: form.photo_urls.split(',').map(s => s.trim()).filter(Boolean),
    }) }),
    onSuccess: (r) => { toast.success(r.status === 'escalated' ? 'Critical · auto-escalated' : 'Ticket filed'); onSaved?.(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="fac-tk-dialog">
        <DialogHeader><DialogTitle>Report issue</DialogTitle><DialogDescription>Files a maintenance ticket into the central ecosystem. Critical priority auto-escalates.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Generator wont start" data-testid="fac-tk-title" /></div>
          <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Priority *</Label>
              <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="fac-tk-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TICKET_PRIORITY.map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Scope *</Label>
              <Select value={form.scope_type} onValueChange={v => setForm({ ...form, scope_type: v })} disabled={!!defaultScope}>
                <SelectTrigger data-testid="fac-tk-scope"><SelectValue /></SelectTrigger>
                <SelectContent>{TICKET_SCOPES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Location</Label><Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="Building / Room" /></div>
          {!defaultScope && <div><Label>Scope reference (optional)</Label><Input value={form.scope_label} onChange={e => setForm({ ...form, scope_label: e.target.value })} placeholder="School name, church name…" /></div>}
          <div><Label>Photos</Label>
            <MediaUploadList
              value={(form.photo_urls || '').split(',').map(s => s.trim()).filter(Boolean)}
              onChange={(list) => setForm({ ...form, photo_urls: list.join(', ') })}
              purpose="photo" hint="Add a photo of this task" testId="fac-tk-photos" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { if (!form.title) return toast.error('Title required'); mut.mutate(); }} disabled={mut.isPending} className="bg-amber-600 hover:bg-amber-700" data-testid="fac-tk-save">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}File ticket
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== PROCUREMENT ====================
function ProcurementTab() {
  const qc = useQueryClient();
  const [view, setView] = useState('prs');
  const [adding, setAdding] = useState(false);
  const [creatingPO, setCreatingPO] = useState(null);
  const { data: prs = [] } = useQuery({ queryKey: ['fac-prs'], queryFn: () => api('/api/facilities/purchase-requests') });
  const { data: pos = [] } = useQuery({ queryKey: ['fac-pos'], queryFn: () => api('/api/facilities/purchase-orders') });
  const { data: suppliers = [] } = useQuery({ queryKey: ['fac-suppliers-active'], queryFn: () => api('/api/facilities/suppliers?active_only=true') });
  const decideMut = useMutation({
    mutationFn: ({ id, approved }) => api(`/api/facilities/purchase-requests/${id}/decision`, { method: 'POST', body: JSON.stringify({ approved }) }),
    onSuccess: () => { toast.success('Updated'); qc.invalidateQueries({ queryKey: ['fac-prs'] }); qc.invalidateQueries({ queryKey: ['fac-dashboard'] }); },
    onError: (e) => toast.error(e.message),
  });
  const receiveMut = useMutation({
    mutationFn: (id) => api(`/api/facilities/purchase-orders/${id}/receive`, { method: 'POST' }),
    onSuccess: (r) => { toast.success(`Received · bank ${r.bank_tx_id ? 'posted ✓' : '(no tx)'}`); qc.invalidateQueries({ queryKey: ['fac-pos'] }); qc.invalidateQueries({ queryKey: ['fac-prs'] }); qc.invalidateQueries({ queryKey: ['fac-inventory'] }); qc.invalidateQueries({ queryKey: ['fac-dashboard'] }); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-2">
          <Button size="sm" variant={view === 'prs' ? 'default' : 'outline'} onClick={() => setView('prs')} data-testid="fac-proc-prs">Purchase Requests</Button>
          <Button size="sm" variant={view === 'pos' ? 'default' : 'outline'} onClick={() => setView('pos')} data-testid="fac-proc-pos">Purchase Orders</Button>
        </div>
        {view === 'prs' && <Button onClick={() => setAdding(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="fac-pr-add"><Plus className="h-4 w-4 mr-1" />New PR</Button>}
      </div>

      {view === 'prs' ? (
        prs.length === 0 ? <Card><CardContent className="p-16 text-center text-slate-400"><ShoppingBag className="h-12 w-12 mx-auto mb-3 opacity-50" /><p className="text-sm">No purchase requests</p></CardContent></Card>
        : <Card><CardContent className="p-0"><table className="w-full text-sm">
            <thead><tr className="text-xs uppercase tracking-wide text-slate-500 border-b">
              <th className="text-left py-2 px-3">Title</th><th className="text-left py-2 px-3">Department</th><th className="text-left py-2 px-3">Requested by</th><th className="text-right py-2 px-3">Total</th><th className="text-left py-2 px-3">Status</th><th className="text-right py-2 px-3">Actions</th>
            </tr></thead>
            <tbody>{prs.map(p => (
              <tr key={p.id} className="border-b last:border-0" data-testid={`fac-pr-${p.id}`}>
                <td className="py-2 px-3"><p className="font-medium">{p.title}</p><p className="text-[10px] text-slate-500">{(p.items || []).length} item{(p.items||[]).length===1?'':'s'}</p></td>
                <td className="py-2 px-3 text-xs">{p.department || '—'}</td>
                <td className="py-2 px-3 text-xs">{p.requested_by_name}</td>
                <td className="py-2 px-3 text-right font-semibold text-emerald-700">{p.currency} {p.total_estimated}</td>
                <td className="py-2 px-3"><Badge variant="outline" className="text-[10px] capitalize">{p.status}</Badge></td>
                <td className="py-2 px-3 text-right">
                  {p.status === 'pending' && (
                    <>
                      <Button size="sm" className="h-7 text-xs mr-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => decideMut.mutate({ id: p.id, approved: true })} data-testid={`fac-pr-approve-${p.id}`}>Approve</Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => decideMut.mutate({ id: p.id, approved: false })} data-testid={`fac-pr-reject-${p.id}`}>Reject</Button>
                    </>
                  )}
                  {p.status === 'approved' && <Button size="sm" className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700" onClick={() => setCreatingPO(p)} data-testid={`fac-pr-makepo-${p.id}`}>Create PO</Button>}
                  {p.po_number && <Badge variant="outline" className="text-[10px] font-mono">{p.po_number}</Badge>}
                </td>
              </tr>
            ))}</tbody>
          </table></CardContent></Card>
      ) : (
        pos.length === 0 ? <Card><CardContent className="p-16 text-center text-slate-400"><ShoppingBag className="h-12 w-12 mx-auto mb-3 opacity-50" /><p className="text-sm">No purchase orders</p></CardContent></Card>
        : <Card><CardContent className="p-0"><table className="w-full text-sm">
            <thead><tr className="text-xs uppercase tracking-wide text-slate-500 border-b">
              <th className="text-left py-2 px-3">PO #</th><th className="text-left py-2 px-3">Supplier</th><th className="text-right py-2 px-3">Total</th><th className="text-left py-2 px-3">Status</th><th className="text-right py-2 px-3"></th>
            </tr></thead>
            <tbody>{pos.map(o => (
              <tr key={o.id} className="border-b last:border-0" data-testid={`fac-po-${o.id}`}>
                <td className="py-2 px-3 font-mono text-xs">{o.po_number}</td>
                <td className="py-2 px-3 text-sm">{o.supplier_name}</td>
                <td className="py-2 px-3 text-right font-semibold text-emerald-700">{o.currency} {o.total}</td>
                <td className="py-2 px-3"><Badge variant="outline" className="text-[10px] capitalize">{o.status}</Badge>{o.bank_tx_id && <Badge className="ml-1 text-[10px] bg-emerald-100 text-emerald-800">bank ✓</Badge>}</td>
                <td className="py-2 px-3 text-right">{o.status !== 'received' && <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => receiveMut.mutate(o.id)} data-testid={`fac-po-receive-${o.id}`}><CheckCircle2 className="h-3 w-3 mr-1" />Receive</Button>}</td>
              </tr>
            ))}</tbody>
          </table></CardContent></Card>
      )}

      {adding && <PRDialog suppliers={suppliers} onClose={() => setAdding(false)} onSaved={() => { qc.invalidateQueries({ queryKey: ['fac-prs'] }); qc.invalidateQueries({ queryKey: ['fac-dashboard'] }); setAdding(false); }} />}
      {creatingPO && <POFromPRDialog pr={creatingPO} suppliers={suppliers} onClose={() => setCreatingPO(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ['fac-pos'] }); qc.invalidateQueries({ queryKey: ['fac-prs'] }); setCreatingPO(null); }} />}
    </div>
  );
}

function PRDialog({ suppliers, onClose, onSaved }) {
  const [form, setForm] = useState({ title: '', department: '', justification: '', currency: 'USD', supplier_id: '' });
  const [items, setItems] = useState([{ name: '', qty: 1, estimated_price: 0 }]);
  const mut = useMutation({
    mutationFn: () => api('/api/facilities/purchase-requests', { method: 'POST', body: JSON.stringify({
      ...form, items: items.filter(i => i.name).map(i => ({ ...i, qty: parseInt(i.qty, 10) || 1, estimated_price: parseFloat(i.estimated_price) || 0 })),
    }) }),
    onSuccess: () => { toast.success('PR submitted'); onSaved?.(); },
    onError: (e) => toast.error(e.message),
  });
  const total = items.reduce((s, i) => s + (parseFloat(i.estimated_price) || 0) * (parseInt(i.qty, 10) || 1), 0);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="fac-pr-dialog">
        <DialogHeader><DialogTitle>New Purchase Request</DialogTitle><DialogDescription>Submit a request for approval. Once approved, a PO can be created.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="fac-pr-title" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Department</Label><Input value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} /></div>
            <div>
              <Label>Preferred supplier</Label>
              <Select value={form.supplier_id} onValueChange={v => setForm({ ...form, supplier_id: v })}>
                <SelectTrigger data-testid="fac-pr-supplier"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Justification</Label><Textarea rows={2} value={form.justification} onChange={e => setForm({ ...form, justification: e.target.value })} /></div>
          <div>
            <div className="flex items-center justify-between mb-1"><Label>Items</Label><Button size="sm" variant="outline" className="h-6 text-xs" onClick={() => setItems([...items, { name: '', qty: 1, estimated_price: 0 }])}>+ row</Button></div>
            {items.map((it, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-1 mb-1.5">
                <Input className="col-span-6 h-8 text-xs" placeholder="Item name" value={it.name} onChange={e => setItems(items.map((x, i) => i === idx ? { ...x, name: e.target.value } : x))} data-testid={`fac-pr-item-name-${idx}`} />
                <Input className="col-span-2 h-8 text-xs" type="number" min={1} value={it.qty} onChange={e => setItems(items.map((x, i) => i === idx ? { ...x, qty: e.target.value } : x))} />
                <Input className="col-span-3 h-8 text-xs" type="number" step="0.01" placeholder="Price" value={it.estimated_price} onChange={e => setItems(items.map((x, i) => i === idx ? { ...x, estimated_price: e.target.value } : x))} />
                <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" onClick={() => setItems(items.filter((_, i) => i !== idx))}><Trash2 className="h-3 w-3" /></Button>
              </div>
            ))}
            <p className="text-right text-sm font-semibold">Total: {form.currency} {total.toFixed(2)}</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { if (!form.title) return toast.error('Title required'); if (items.filter(i => i.name).length === 0) return toast.error('At least one item'); mut.mutate(); }} disabled={mut.isPending} className="bg-emerald-600 hover:bg-emerald-700" data-testid="fac-pr-save">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function POFromPRDialog({ pr, suppliers, onClose, onSaved }) {
  const [supplierId, setSupplierId] = useState(pr.supplier_id || '');
  const mut = useMutation({
    mutationFn: () => api('/api/facilities/purchase-orders', { method: 'POST', body: JSON.stringify({
      pr_id: pr.id, supplier_id: supplierId,
      items: (pr.items || []).map(i => ({ name: i.name, qty: i.qty, price: i.estimated_price, item_id: i.item_id })),
      currency: pr.currency, total: pr.total_estimated,
    }) }),
    onSuccess: (r) => { toast.success(`PO ${r.po_number} created`); onSaved?.(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="fac-po-dialog">
        <DialogHeader><DialogTitle>Create PO from PR</DialogTitle><DialogDescription>Locks in supplier, generates a PO number, links back to PR.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm"><span className="font-semibold">{pr.title}</span> · {pr.currency} {pr.total_estimated}</p>
          <div>
            <Label>Supplier *</Label>
            <Select value={supplierId} onValueChange={setSupplierId}>
              <SelectTrigger data-testid="fac-po-supplier"><SelectValue placeholder="Choose supplier" /></SelectTrigger>
              <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { if (!supplierId) return toast.error('Supplier required'); mut.mutate(); }} disabled={mut.isPending} className="bg-indigo-600 hover:bg-indigo-700" data-testid="fac-po-save">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Create PO
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== SUPPLIERS ====================
function SuppliersTab() {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const { data: suppliers = [] } = useQuery({ queryKey: ['fac-suppliers'], queryFn: () => api('/api/facilities/suppliers') });
  const delMut = useMutation({
    mutationFn: (id) => api(`/api/facilities/suppliers/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Removed'); qc.invalidateQueries({ queryKey: ['fac-suppliers'] }); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <div className="space-y-3">
      <div className="flex justify-end"><Button onClick={() => setAdding(true)} className="bg-teal-600 hover:bg-teal-700" data-testid="fac-sup-add"><Plus className="h-4 w-4 mr-1" />New supplier</Button></div>
      {suppliers.length === 0 ? <Card><CardContent className="p-16 text-center text-slate-400"><Truck className="h-12 w-12 mx-auto mb-3 opacity-50" /><p className="text-sm">No suppliers</p></CardContent></Card>
       : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {suppliers.map(s => (
            <Card key={s.id} className={s.is_active ? '' : 'opacity-60'} data-testid={`fac-sup-${s.id}`}>
              <CardContent className="p-3">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-sm">{s.name}</p>
                    {s.category && <p className="text-[11px] text-slate-500 capitalize">{s.category}</p>}
                  </div>
                  {!s.is_active && <Badge variant="outline" className="text-[9px]">Inactive</Badge>}
                </div>
                {s.email && <p className="text-[11px] text-slate-500 mt-1">{s.email}</p>}
                {s.phone && <p className="text-[11px] text-slate-500">{s.phone}</p>}
                <div className="flex justify-end gap-1 mt-2 pt-2 border-t">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditing(s)} data-testid={`fac-sup-edit-${s.id}`}><Edit3 className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600" onClick={() => { if (window.confirm(`Delete ${s.name}?`)) delMut.mutate(s.id); }} data-testid={`fac-sup-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>}
      {adding && <SupplierDialog onClose={() => setAdding(false)} onSaved={() => { qc.invalidateQueries({ queryKey: ['fac-suppliers'] }); setAdding(false); }} />}
      {editing && <SupplierDialog supplier={editing} onClose={() => setEditing(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ['fac-suppliers'] }); setEditing(null); }} />}
    </div>
  );
}

function SupplierDialog({ supplier, onClose, onSaved }) {
  const isEdit = !!supplier;
  const [form, setForm] = useState(supplier ? {
    name: supplier.name || '', category: supplier.category || '', contact_name: supplier.contact_name || '',
    email: supplier.email || '', phone: supplier.phone || '', address: supplier.address || '',
    website: supplier.website || '', notes: supplier.notes || '', is_active: supplier.is_active !== false,
  } : { name: '', category: '', contact_name: '', email: '', phone: '', address: '', website: '', notes: '', is_active: true });
  const mut = useMutation({
    mutationFn: () => api(isEdit ? `/api/facilities/suppliers/${supplier.id}` : '/api/facilities/suppliers',
      { method: isEdit ? 'PUT' : 'POST', body: JSON.stringify(form) }),
    onSuccess: () => { toast.success(isEdit ? 'Saved' : 'Created'); onSaved?.(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="fac-sup-dialog">
        <DialogHeader><DialogTitle>{isEdit ? 'Edit supplier' : 'New supplier'}</DialogTitle><DialogDescription>Vendor directory used by procurement.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2"><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="fac-sup-name" /></div>
          <div><Label>Category</Label><Input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} /></div>
          <div><Label>Contact name</Label><Input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} /></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
          <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
          <div className="col-span-2"><Label>Address</Label><Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></div>
          <div className="col-span-2"><Label>Website</Label><Input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} /></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          <label className="col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} className="rounded" />Active</label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { if (!form.name) return toast.error('Name required'); mut.mutate(); }} disabled={mut.isPending} className="bg-teal-600 hover:bg-teal-700" data-testid="fac-sup-save">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}{isEdit ? 'Save' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
